import { Store } from 'vuex';

import {
  MenuItemGroup,
  Menu,
  BuilderMode,
  MealBuilderItemSection,
  SelectableMenuItem,
  SingleEntreeTypeBuilderMode,
  BuilderModeKind,
  SelectableVariantMenuItem,
  ZeroVariantBuilderMode,
  ItemCardCustomization,
  MenuMetadataForChannel,
  MultiselectSingleEntreeTypeBuilderMode,
  PriceType,
} from '@/models';

import { AppState } from '@/store';
import { MUTATIONS } from '@/store/meal-builder';
import { Logger } from '@/services/logger/logger';

import { MealBuilderVariantService, AbstractMealBuilderItemVariantService } from './meal-builder-variant.service';
import { MealBuilderItemSelector } from './item-selectors';
import { MealBuilderModeMapper } from './modes/meal-builder-mode-mapper.service';
import { UtilsService } from '@/services/utils/utils.service';
import { MenuService } from '@/services/menu/menu.service';
import { MealBuilderAnalyticService } from './meal-builder-analytics.service';
import { AnalyticsService } from '../analytics/analytics.service';

export interface MealBuilderService extends MealBuilderVariantService<BuilderMode>, MealBuilderItemSelector, MealBuilderModeMapper {
  refreshBuilder(menu: Menu, metadata: MenuMetadataForChannel, group: MenuItemGroup, isEditMode?: boolean): void;
  resetBuilderMode(): void;
  setCustomizingItem(item: SelectableMenuItem | null): void;
  setActiveItemId(itemId: string | null): void;
  setErrorEventSent(event: string, value: boolean): void;
  getErrorEventSent(event: string): boolean;
}

export class AppMealBuilderService extends AbstractMealBuilderItemVariantService<BuilderMode> implements MealBuilderService {
  private errorEventsSent: { [key: string]: boolean } = {};
  constructor(
    utils: UtilsService,
    menu: MenuService,
    store: Store<AppState>,
    private singleEntreeService: MealBuilderVariantService<SingleEntreeTypeBuilderMode>,
    private multiselectSingleEntreeService: MealBuilderVariantService<MultiselectSingleEntreeTypeBuilderMode>,
    private zeroVariantService: MealBuilderVariantService<ZeroVariantBuilderMode>,
    private builderModeMapper: MealBuilderModeMapper,
    itemSelector: MealBuilderItemSelector,
    logger: Logger,
    mealBuilderAnalytics: MealBuilderAnalyticService,
    analytics: AnalyticsService,
  ) {
    super(utils, menu, store, itemSelector, logger, mealBuilderAnalytics, analytics);
  }

  selectVariantSafe(
    builderMode: BuilderMode,
    chosenVariant: SelectableVariantMenuItem,
    currentVariant: SelectableVariantMenuItem | null,
    allVariants: SelectableVariantMenuItem[],
    args: any,
    shouldTrackAnalytics?: boolean,
    isEditMode?: boolean,
  ) {
    const service = this.getVariantServiceForMode(builderMode.mode);
    
    service.selectVariantSafe(builderMode, chosenVariant, currentVariant, allVariants, args, shouldTrackAnalytics, isEditMode);
    this.refreshAvailableVariants(builderMode);
  }

  selectDefaultContents(builderMode: BuilderMode, variant: SelectableVariantMenuItem) {
    const service = this.getVariantServiceForMode(builderMode.mode);

    service.selectDefaultContents(builderMode, variant);
  }

  toggleVariantCustomizationSafe(
    builderMode: BuilderMode,
    variant: SelectableVariantMenuItem,
    allVariants: SelectableVariantMenuItem[],
    customization: ItemCardCustomization,
    shouldTrackAnalytics?: boolean,
  ) {
    const service = this.getVariantServiceForMode(builderMode.mode);

    return service.toggleVariantCustomizationSafe(builderMode, variant, allVariants, customization, shouldTrackAnalytics);
  }

  getItemSections(builderMode: BuilderMode, variant: SelectableVariantMenuItem): MealBuilderItemSection[] {
    const service = this.getVariantServiceForMode(builderMode.mode);

    return service.getItemSections(builderMode, variant);
  }

  getBuilderMode(menu: Menu, metadata: MenuMetadataForChannel, group: MenuItemGroup): BuilderMode {
    return this.builderModeMapper.getBuilderMode(menu, metadata, group);
  }

  refreshAvailableVariants(builderMode: BuilderMode) {
    const service = this.getVariantServiceForMode(builderMode.mode);
    const variants = service.getVariants(builderMode);
    this.store.commit(MUTATIONS.SET_DISPLAYED_VARIANTS, variants);
  }

  refreshBuilder(menu: Menu, metadata: MenuMetadataForChannel, group: MenuItemGroup, isEditMode = false) {
    this.resetBuilderMode();

    const mode = this.refreshBuilderMode(menu, metadata, group, isEditMode);
    this.refreshAvailableVariants(mode);
  }


  setErrorEventSent(event: string, value: boolean): void {
    this.errorEventsSent[event] = value;
  }

  getErrorEventSent(event: string): boolean {
    return this.errorEventsSent[event] || false;
  }

  refreshBuilderMode(menu: Menu, metadata: MenuMetadataForChannel, group: MenuItemGroup, isEditMode: boolean): BuilderMode {
    const mode = this.getBuilderMode(menu, metadata, group);
    const service = this.getVariantServiceForMode(mode.mode);

    service.setBuilderMode(mode);

    const defaultVariant = service.getDefaultVariant(mode);
    if (defaultVariant && !isEditMode) {
      service.selectDefaultContents(mode, defaultVariant);
    }

    return mode;
  }

  resetBuilderMode() {
    this.itemSelector.resetItemSelections();
    this.setSelectedVariant(null);
    this.setBuilderMode(null);
    this.setMissingSections([]);
  }

  getVariantsItemSectionTitle(builderMode: BuilderMode): string {
    const service = this.getVariantServiceForMode(builderMode.mode);

    return service.getVariantsItemSectionTitle(builderMode);
  }

  getVariantPriceType(builderMode: BuilderMode): PriceType {
      const service = this.getVariantServiceForMode(builderMode.mode);

      return service.getVariantPriceType(builderMode);
  }

  getVariantsItemSectionSubtitle(builderMode: BuilderMode, variant: SelectableVariantMenuItem): string | undefined {
    const service = this.getVariantServiceForMode(builderMode.mode);
    
    return service.getVariantsItemSectionSubtitle(builderMode, variant);
  }

  isItemDisplayedAsSelected(builderMode: BuilderMode, item: SelectableMenuItem) {
    const service = this.getVariantServiceForMode(builderMode.mode);

    return service.isItemDisplayedAsSelected(builderMode, item);
  }

  getDisplayedItemPortion(builderMode: BuilderMode, variant: SelectableMenuItem, currentVariant: SelectableVariantMenuItem | null) {
    const service = this.getVariantServiceForMode(builderMode.mode);

    return service.getDisplayedItemPortion(builderMode, variant, currentVariant);
  }

  getVariants(builderMode: BuilderMode): SelectableVariantMenuItem[] {
    const service = this.getVariantServiceForMode(builderMode.mode);

    return service.getVariants(builderMode);
  }

  getDefaultVariant(builderMode: BuilderMode) {
    const service = this.getVariantServiceForMode(builderMode.mode);

    return service.getDefaultVariant(builderMode);
  }

  validateMealBuilder(builderMode: BuilderMode, metadata: MenuMetadataForChannel, itemSections: MealBuilderItemSection[]) {
    const service = this.getVariantServiceForMode(builderMode.mode);

    return service.validateMealBuilder(builderMode, metadata, itemSections);
  }

  buildMealRequest(mode: BuilderMode, items: SelectableMenuItem[], mealName: string) {
    const service = this.getVariantServiceForMode(mode.mode);

    return service.buildMealRequest(mode, items, mealName);
  }

  getEffectiveItemPrice(builderMode: BuilderMode, item: SelectableMenuItem, variant: SelectableVariantMenuItem | null) {
    const service = this.getVariantServiceForMode(builderMode.mode);

    return service.getEffectiveItemPrice(builderMode, item, variant);
  }

  selectItemSafe<TArgs>(item: SelectableMenuItem, itemSection: MealBuilderItemSection | null, variant: SelectableVariantMenuItem, args: TArgs, showInformationalMessage?: boolean, shouldTrackAnalytics?: boolean) {
    return this.itemSelector.selectItemSafe(item, itemSection, variant, args, showInformationalMessage, shouldTrackAnalytics);
  }

  toggleItemCustomizationSafe(
    item: SelectableMenuItem,
    customization: ItemCardCustomization,
    itemSection: MealBuilderItemSection | null,
    variant: SelectableVariantMenuItem,
    showInformationalMessage?: boolean,
    shouldTrackAnalytics?: boolean,
  ) {
    return this.itemSelector.toggleItemCustomizationSafe(item, customization, itemSection, variant, showInformationalMessage, shouldTrackAnalytics);
  }

  getAllSelectedItems(): SelectableMenuItem[] {
    return this.itemSelector.getAllSelectedItems();
  }

  getSelectedItems(items: SelectableMenuItem[]): SelectableMenuItem[] {
    return this.itemSelector.getSelectedItems(items);
  }

  deselectAllItems(items: SelectableMenuItem[], shallowDeselect?: boolean | undefined): void {
    return this.itemSelector.deselectAllItems(items, shallowDeselect);
  }

  resetItemSelections(): void {
    return this.itemSelector.resetItemSelections();
  }

  selectItemShallow(item: SelectableMenuItem, args?: any): boolean {
    return this.itemSelector.selectItemShallow(item, args);
  }

  synchronizeItems(items: SelectableMenuItem[]): void {
    return this.itemSelector.synchronizeItems(items);
  }

  selectItem(item: SelectableMenuItem, args?: any): boolean {
    return this.itemSelector.selectItem(item, args);
  }

  deselectItem(item: SelectableMenuItem): void {
    return this.itemSelector.deselectItem(item);
  }

  getItemQuantity(item: SelectableMenuItem): number {
    return this.itemSelector.getItemQuantity(item);
  }

  getEffectiveItemQuantity(item: SelectableMenuItem): number {
    return this.itemSelector.getEffectiveItemQuantity(item);
  }

  getEffectiveItemSectionQuantity(itemSection: MealBuilderItemSection): number {
    return this.itemSelector.getEffectiveItemSectionQuantity(itemSection);
  }

  optOutOfDefaultContentItem(itemId: string) {
    return this.itemSelector.optOutOfDefaultContentItem(itemId);
  }

  toggleOptOutOfItemSection(section: MealBuilderItemSection, shouldTrackAnalytics?: boolean) {
    return this.itemSelector.toggleOptOutOfItemSection(section, shouldTrackAnalytics);
  }

  optOutOfItemSection(section: MealBuilderItemSection, shouldTrackAnalytics?: boolean) {
    return this.itemSelector.optOutOfItemSection(section, shouldTrackAnalytics);
  }

  removeOptOutOfItemSection(section: MealBuilderItemSection, shouldTrackAnalytics?: boolean) {
    return this.itemSelector.removeOptOutOfItemSection(section, shouldTrackAnalytics);
  }

  getMaxQuantity(item: SelectableMenuItem): number {
    return this.itemSelector.getMaxQuantity(item);
  }

  isItemSelected(item: SelectableMenuItem): boolean {
    return this.itemSelector.isItemSelected(item);
  }

  isDeselecting(item: SelectableMenuItem, args: any): boolean {
    return this.itemSelector.isDeselecting(item, args);
  }

  getQuantityAfterSelection(item: SelectableMenuItem, args: any): number {
    return this.itemSelector.getQuantityAfterSelection(item, args);
  }

  copySelectionTo(target: SelectableMenuItem, src: SelectableMenuItem, shallowSelection: boolean): boolean {
    return this.itemSelector.copySelectionTo(target, src, shallowSelection);
  }

  getVariantServiceForMode(mode: BuilderModeKind): MealBuilderVariantService<any> {
    switch (mode) {
      case BuilderModeKind.SingleEntreeType:
        return this.singleEntreeService;

      case BuilderModeKind.MultiselectSingleEntreeType:
        return this.multiselectSingleEntreeService;

      case BuilderModeKind.ZeroVariant:
        return this.zeroVariantService;

      default:
        const error = `Unknown builder mode: ${mode}`;
        this.logger.unreachable(error);

        throw new Error(error);
    }
  }

  setCustomizingItem(item: SelectableMenuItem | null) {
    this.store.commit(MUTATIONS.SET_CUSTOMIZING_ITEM, item);
  }

  setBuilderMode(mode: BuilderMode | null) {
    this.store.commit(MUTATIONS.SET_BUILDER_MODE, mode);
  }

  setMissingSections(sections: string[]) {
    this.store.commit(MUTATIONS.SET_MISSING_ITEM_SECTIONS, sections);
  }

  setActiveItemId(itemId: string | null) {
    this.store.commit(MUTATIONS.SET_ACTIVE_ITEM, itemId);
  }
}
